import { ExpandingRadio } from "../expanding-radio/ExpandingRadio"

type Props = {
  vehicleCondition: string
  addVehicleCondition: Function
  rendersAdditionalText?: boolean
}

enum Condition {
  excellent = "Excellent",
  good = "Good",
  average = "Average",
  belowAverage = "Below Average",
}

const VehicleCondition: React.FC<Props> = ({
  vehicleCondition,
  addVehicleCondition,
  rendersAdditionalText = true,
}) => {
  const onChange = (e: React.FormEvent<HTMLInputElement>) =>
    addVehicleCondition(e.currentTarget.value)

  return (
    <div>
      <h2 className="vehicle-condition-h2">Vehicle condition</h2>
      {rendersAdditionalText ? (
        <h3 className="vehicle-condition-h3">
          Select your vehicle’s condition for further detail
        </h3>
      ) : null}

      <ExpandingRadio
        label="Excellent"
        caption="Interior and exterior virtually unmarked."
        value={Condition.excellent}
        checked={vehicleCondition === Condition.excellent}
        onChange={onChange}
        revealChildrenWhen={vehicleCondition === Condition.excellent}
      >
        <div>
          <h4 className="additional-header">Example condition:</h4>
          <ul className="additional-list">
            <li>
              Full manufacturer service history as required for age and mileage.
            </li>
            <li>Unblemished interior condition.</li>
            <li>1 small chip or scratch that doesn't go through the paint.</li>
            <li>1 tyre change required.</li>
            <li>
              1 diamond cut alloy in need of refurbishment OR 2 standard alloys
              in need of refurbishment.
            </li>
            <li>At least 2 keys.</li>
          </ul>
        </div>
      </ExpandingRadio>

      <ExpandingRadio
        label="Good"
        caption="Minor cosmetic damage."
        value={Condition.good}
        checked={vehicleCondition === Condition.good}
        onChange={onChange}
        revealChildrenWhen={vehicleCondition === Condition.good}
      >
        <div>
          <h4 className="additional-header">Example condition:</h4>
          <ul className="additional-list">
            <li>
              Full service history in accordance with manufacturer schedule.
            </li>
            <li>
              Up to 3 small scratches or dents that don't penetrate the paint.
            </li>
            <li>1 scratch, chip or dent that penetrates the paint.</li>
            <li>A good interior condition.</li>
            <li>At least 2 keys.</li>
            <li>1 tyre change required.</li>
            <li>Up to 2 standard alloys in need of refurbishment.</li>
          </ul>
        </div>
      </ExpandingRadio>

      <ExpandingRadio
        label="Average"
        caption="Visible wear & tear."
        value={Condition.average}
        checked={vehicleCondition === Condition.average}
        onChange={onChange}
        revealChildrenWhen={vehicleCondition === Condition.average}
      >
        <div>
          <h4 className="additional-header">Example condition:</h4>
          <ul className="additional-list">
            <li>Some service history.</li>
            <li>Up to 3 larger scratches and chips through the paint.</li>
            <li>Up to 3 small dents or one dent up to 50mm.</li>
            <li>An average interior condition.</li>
            <li>Up to 2 tyre changes required.</li>
            <li>Up to 2 alloys that need to be refurbished.</li>
          </ul>
        </div>
      </ExpandingRadio>

      <ExpandingRadio
        label="Below Average"
        caption="Vehicle would require significant attention."
        value={Condition.belowAverage}
        checked={vehicleCondition === Condition.belowAverage}
        onChange={onChange}
        revealChildrenWhen={vehicleCondition === Condition.belowAverage}
      >
        <div>
          <h4 className="additional-header">Example condition:</h4>
          <ul className="additional-list">
            <li>Unknown service history.</li>
            <li>A windscreen with 2 or more chips.</li>
            <li>Clear Interior wear and tear.</li>
            <li>1 panel or bumper to be refurbished.</li>
            <li>Up to 2 areas of bodywork corrosion.</li>
            <li>Up to 4 scratches penetrating the paintwork.</li>
            <li>Up to 2 tyres that need to be replaced.</li>
            <li>Up to 4 alloys that need to be refurbished.</li>
          </ul>
        </div>
      </ExpandingRadio>
    </div>
  )
}

export { VehicleCondition }
